import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  FiHelpCircle,
  FiMenu,
  FiSearch,
  FiShoppingBag,
  FiX,
} from "react-icons/fi";
import { useCart } from "../pages/CartContext";

const products = [
  { id: "gray", name: "White Runner", keywords: "white gray tonal runner" },
  { id: "yellow", name: "Burgundy Runner", keywords: "burgundy wine yellow runner" },
  { id: "topwhite", name: "White / Black Runner", keywords: "white black contrast runner" },
  { id: "black", name: "Black Runner", keywords: "black monochrome runner" },
];

const navLinks = [
  { label: "Collection", to: "/#collection" },
  { label: "About", to: "/about" },
  { label: "Feedback", to: "/feedback" },
];

const Navbar = () => {
  const [searchOpen, setSearchOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const { cart } = useCart();
  const navigate = useNavigate();
  const location = useLocation();

  const cartCount = cart.reduce(
    (total, item) => total + Number(item.quantity || 1),
    0
  );

  const filteredProducts = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return products;

    return products.filter(({ name, keywords }) =>
      `${name} ${keywords}`.toLowerCase().includes(query)
    );
  }, [searchQuery]);

  useEffect(() => {
    setMobileMenuOpen(false);
    setSearchOpen(false);
    setSearchQuery("");
  }, [location.pathname]);

  useEffect(() => {
    if (!searchOpen && !mobileMenuOpen) return undefined;

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        setSearchOpen(false);
        setMobileMenuOpen(false);
      }
    };

    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, [searchOpen, mobileMenuOpen]);

  const closeMenus = () => {
    setSearchOpen(false);
    setMobileMenuOpen(false);
    setSearchQuery("");
  };

  const goToCollection = () => {
    closeMenus();

    if (location.pathname === "/") {
      document.getElementById("collection")?.scrollIntoView({ behavior: "smooth" });
      return;
    }

    navigate("/");
    window.setTimeout(() => {
      document.getElementById("collection")?.scrollIntoView({ behavior: "smooth" });
    }, 100);
  };

  const handleSearchSubmit = (event) => {
    event.preventDefault();

    if (filteredProducts.length > 0) {
      navigate(`/product/${filteredProducts[0].id}`);
      closeMenus();
    }
  };

  return (
    <header className="sticky top-0 z-50 bg-[#171713] text-[#f4f1eb]">
      <div className="border-b border-white/10 bg-black px-4 py-2 text-center text-[9px] uppercase tracking-[0.24em] text-white/70 sm:text-[10px] sm:tracking-[0.32em]">
        Limited runner collection · Complimentary U.S. shipping
      </div>

      <div className="relative border-b border-white/10">
        <div className="mx-auto flex h-[72px] max-w-7xl items-center justify-between px-5 sm:px-8 lg:px-12">
          <button
            type="button"
            className="flex h-10 w-10 items-center justify-start text-white md:hidden"
            onClick={() => {
              setMobileMenuOpen((open) => !open);
              setSearchOpen(false);
            }}
            aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
            aria-expanded={mobileMenuOpen}
          >
            {mobileMenuOpen ? <FiX size={21} /> : <FiMenu size={21} />}
          </button>

          <Link
            to="/"
            onClick={closeMenus}
            className="font-serif text-xl tracking-[0.14em] transition-opacity hover:opacity-70 sm:text-2xl"
            aria-label="PΛREΛGON home"
          >
            PΛREΛGON
          </Link>

          <nav className="absolute left-1/2 hidden -translate-x-1/2 items-center gap-8 md:flex">
            {navLinks.map((link) =>
              link.to === "/#collection" ? (
                <button
                  key={link.label}
                  type="button"
                  onClick={goToCollection}
                  className="text-[10px] uppercase tracking-[0.25em] text-white/70 transition hover:text-white"
                >
                  {link.label}
                </button>
              ) : (
                <Link
                  key={link.label}
                  to={link.to}
                  className="text-[10px] uppercase tracking-[0.25em] text-white/70 transition hover:text-white"
                >
                  {link.label}
                </Link>
              )
            )}
          </nav>

          <div className="flex items-center gap-1 sm:gap-2">
            <Link
              to="/help"
              className="hidden h-10 w-10 items-center justify-center text-white/75 transition hover:text-white sm:flex"
              aria-label="Help"
            >
              <FiHelpCircle size={19} />
            </Link>

            <button
              type="button"
              onClick={() => {
                setSearchOpen((open) => !open);
                setMobileMenuOpen(false);
              }}
              className="flex h-10 w-10 items-center justify-center text-white/75 transition hover:text-white"
              aria-label={searchOpen ? "Close product search" : "Search products"}
              aria-expanded={searchOpen}
            >
              {searchOpen ? <FiX size={20} /> : <FiSearch size={19} />}
            </button>

            <button
              type="button"
              onClick={() => navigate("/checkout")}
              className="relative flex h-10 w-10 items-center justify-center text-white/75 transition hover:text-white"
              aria-label={`Shopping bag with ${cartCount} ${cartCount === 1 ? "item" : "items"}`}
            >
              <FiShoppingBag size={19} />
              {cartCount > 0 && (
                <span className="absolute right-0 top-0 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-[#f4f1eb] px-1 text-[9px] font-semibold text-black">
                  {cartCount > 99 ? "99+" : cartCount}
                </span>
              )}
            </button>
          </div>
        </div>

        {searchOpen && (
          <div className="absolute inset-x-0 top-full border-b border-white/10 bg-[#171713] shadow-2xl">
            <div className="mx-auto max-w-3xl px-5 py-7 sm:px-8">
              <form onSubmit={handleSearchSubmit} className="border-b border-white/30">
                <div className="flex items-center gap-3">
                  <FiSearch className="shrink-0 text-white/45" size={18} />
                  <input
                    type="search"
                    autoFocus
                    value={searchQuery}
                    onChange={(event) => setSearchQuery(event.target.value)}
                    placeholder="Search the runner collection"
                    className="w-full bg-transparent py-4 text-sm font-light tracking-wide text-white outline-none placeholder:text-white/35"
                    aria-label="Search the runner collection"
                  />
                  <button
                    type="submit"
                    disabled={filteredProducts.length === 0}
                    className="text-[9px] uppercase tracking-[0.24em] text-white/60 transition hover:text-white disabled:cursor-not-allowed disabled:opacity-25"
                  >
                    Open
                  </button>
                </div>
              </form>

              <div className="pt-5">
                <p className="mb-3 text-[9px] uppercase tracking-[0.28em] text-white/35">
                  {searchQuery ? "Results" : "The collection"}
                </p>

                {filteredProducts.length > 0 ? (
                  <div className="grid gap-px overflow-hidden border border-white/10 sm:grid-cols-2">
                    {filteredProducts.map((product) => (
                      <Link
                        key={product.id}
                        to={`/product/${product.id}`}
                        onClick={closeMenus}
                        className="flex items-center justify-between bg-white/[0.02] px-4 py-4 text-xs tracking-wide text-white/75 transition hover:bg-white hover:text-black"
                      >
                        <span>{product.name}</span>
                        <span aria-hidden="true">→</span>
                      </Link>
                    ))}
                  </div>
                ) : (
                  <p className="py-4 text-sm font-light text-white/50">
                    No runners match “{searchQuery}.”
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {mobileMenuOpen && (
          <div className="absolute inset-x-0 top-full border-b border-white/10 bg-[#171713] shadow-2xl md:hidden">
            <nav className="px-5 py-6 sm:px-8">
              {navLinks.map((link) =>
                link.to === "/#collection" ? (
                  <button
                    key={link.label}
                    type="button"
                    onClick={goToCollection}
                    className="flex w-full items-center justify-between border-b border-white/10 py-5 text-left text-xs uppercase tracking-[0.24em] text-white/75"
                  >
                    {link.label}
                    <span aria-hidden="true">→</span>
                  </button>
                ) : (
                  <Link
                    key={link.label}
                    to={link.to}
                    onClick={closeMenus}
                    className="flex items-center justify-between border-b border-white/10 py-5 text-xs uppercase tracking-[0.24em] text-white/75"
                  >
                    {link.label}
                    <span aria-hidden="true">→</span>
                  </Link>
                )
              )}

              <Link
                to="/help"
                onClick={closeMenus}
                className="flex items-center justify-between py-5 text-xs uppercase tracking-[0.24em] text-white/75"
              >
                Help
                <span aria-hidden="true">→</span>
              </Link>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Navbar;